import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { SelectPrograma } from "@/components/ui/SelectPrograma";
import {
  createActividadProgramaSchema,
  type CreateActividadProgramaInput,
} from "@/lib/validaciones/createActividadPrograma.validacion";
import { useRegisterActividadPrograma } from "@/hooks/actividadesProgramas/useRegisterActividadPrograma";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useProgramaById } from "@/hooks/programas/useProgramas";
import { Textarea } from "@/components/ui/textarea";
import { useCurrentUser } from "@/features/login/hooks/useCurrentUser";
import { ActividadProgramaArchivoField } from "@/components/forms/actividades-programas/ActividadProgramaArchivoField";
import { uploadActividadProgramaArchivo } from "@/components/forms/actividades-programas/uploadActividadProgramaArchivo";

export function RegisterActividadProgramaForm({
  idPrograma,
  setOpen,
  refetch,
}: {
  idPrograma?: number;
  setOpen: (v: boolean) => void;
  refetch: () => void;
}) {
  const { user } = useCurrentUser();
  const isSuperAdmin = user?.rol?.nombre === "SUPER_ADMIN";

  const [archivoLimpioFile, setArchivoLimpioFile] = useState<File | null>(null);
  const [archivoWordFile, setArchivoWordFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const form = useForm<CreateActividadProgramaInput>({
    resolver: zodResolver(createActividadProgramaSchema),
    defaultValues: {
      idPrograma: idPrograma ?? 0,
      nombre: "",
      rol: "",
      archivoLimpio: null,
      archivoWord: null,
      fechaInicio: null,
      fechaFin: null,
    },
  });

  const { mutate } = useRegisterActividadPrograma();
  const { data: programaSeleccionado } = useProgramaById(
    form.watch("idPrograma"),
  );

  const onSubmit = async (data: CreateActividadProgramaInput) => {
    setIsSubmitting(true);
    try {
      let archivoLimpio = undefined;
      let archivoWord = undefined;
    if (programaSeleccionado?.cerebro) {
      if (archivoLimpioFile) {
        archivoLimpio = await uploadActividadProgramaArchivo(
          archivoLimpioFile,
          programaSeleccionado as { cerebro: string },
          data.nombre,
          "pdf",
        );
      }
      if (archivoWordFile) {
        archivoWord = await uploadActividadProgramaArchivo(
          archivoWordFile,
          programaSeleccionado as { cerebro: string },
          data.nombre,
          "word",
        );
      }
    }
    mutate(
      {
        ...data,
        archivoLimpio,
        archivoWord,
      },
      {
        onSuccess: () => {
          refetch();
          setOpen(false);
          form.reset();
          setArchivoLimpioFile(null);
          setArchivoWordFile(null);
        },
        onSettled: () => {
          setIsSubmitting(false);
        },
      },
    );
    } catch {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4 sm:space-y-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          {typeof idPrograma === "undefined" && (
            <FormField
              control={form.control}
              name="idPrograma"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>Programa</FormLabel>
                  <FormControl>
                    <SelectPrograma
                      value={
                        field.value
                          ? { value: String(field.value), label: "" }
                          : null
                      }
                      onChange={(option) =>
                        field.onChange(option ? Number(option.value) : 0)
                      }
                      placeholder="Selecciona un programa..."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="nombre"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Nombre</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Campo "Rol del Usuario" solo visible para SUPER_ADMIN */}
          {isSuperAdmin && (
            <FormField
            control={form.control}
            name="rol"
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel>Rol del Usuario</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value === "" ? null : value);
                    }}
                    placeholder="Describe el rol o función que desempeñará el usuario en esta actividad..."
                    className="min-h-[80px] max-h-[200px] resize-y overflow-y-auto"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          )}

          <ActividadProgramaArchivoField
            label="Archivo limpio (*.pdf)"
            accept=".pdf"
            inputId="archivo-limpio-upload"
            selectedFile={archivoLimpioFile}
            onFileChange={setArchivoLimpioFile}
          />
          <ActividadProgramaArchivoField
            label="Archivo Word (*.doc, *.docx)"
            accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            inputId="archivo-word-upload"
            selectedFile={archivoWordFile}
            onFileChange={setArchivoWordFile}
          />

          {["fechaInicio", "fechaFin"].map((campo) => (
            <FormField
              key={campo}
              control={form.control}
              name={campo as "fechaInicio" | "fechaFin"}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {campo === "fechaInicio"
                      ? "Fecha de inicio"
                      : "Fecha de fin"}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      value={
                        field.value
                          ? new Date(field.value).toISOString().split("T")[0]
                          : ""
                      }
                      onChange={(e) => field.onChange(new Date(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-10 sm:h-11 text-sm sm:text-base"
        >
          {isSubmitting ? "Creando..." : "Crear Actividad"}
        </Button>
      </form>
    </Form>
  );
}
